import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  ActionModal,
  NoticeboardButton,
  ACTION_MODAL_FOOTER_PRIMARY_SIZE,
} from "../../shared";
import type {
  ChildReportCard,
  ParentChild,
  ReportSubjectRow,
  ReportTermKey,
} from "../../../types/parentFamily";
import { getChildFullName } from "../../../utils/parentFamilyHelpers";
import {
  REPORT_PHASE_OPTIONS,
  REPORT_TERM_LABELS,
  REPORT_TITLE_OPTIONS,
  academicYearOptions,
  buildPeriodLabel,
  capsLevelForPercent,
  createSubjectRow,
  defaultSubjects,
  detectPrimaryTerm,
  normalizeReportCard,
} from "../../../utils/reportCardCaps";
import { lightTheme } from "../../../styles/theme";

export interface ReportCardFormValues {
  id?: string;
  childId?: string;
  /** Derived: "Term 2 2026" */
  periodLabel: string;
  title: string;
  phase: string;
  academicYear: string;
  /** Single term this card captures marks for */
  termKey: ReportTermKey;
  daysAbsent: string;
  subjects: ReportSubjectRow[];
  teacherComment: string;
  teacherSignedAt: string;
  principalComment: string;
  principalSignedAt: string;
  schoolClosesOn: string;
  schoolReopensOn: string;
  overallResult: string;
  issuedDate: string;
  notes: string;
}

interface ReportCardFormModalProps {
  open: boolean;
  onClose: () => void;
  initialReport?: ChildReportCard | null;
  childrenOptions?: Pick<ParentChild, "id" | "firstName" | "lastName">[];
  selectedChildId?: string | null;
  onChildChange?: (childId: string) => void;
  onSave: (values: ReportCardFormValues) => void;
}

const TERM_SELECT_OPTIONS: ReportTermKey[] = ["term1", "term2", "term3"];

const emptyForm = (childId?: string): ReportCardFormValues => {
  const academicYear = String(new Date().getFullYear());
  const termKey: ReportTermKey = "term2";
  return {
    childId,
    periodLabel: buildPeriodLabel(termKey, academicYear),
    title: "Progress Report",
    phase: "Intermediate Phase",
    academicYear,
    termKey,
    daysAbsent: "",
    subjects: defaultSubjects(),
    teacherComment: "",
    teacherSignedAt: "",
    principalComment: "",
    principalSignedAt: "",
    schoolClosesOn: "",
    schoolReopensOn: "",
    overallResult: "",
    issuedDate: "",
    notes: "",
  };
};

function parseOptionalNumber(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

const AchievementBadge: React.FC<{ percent: number | null | undefined }> = ({
  percent,
}) => {
  const level = capsLevelForPercent(percent);
  if (!level) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 36 }}>
        —
      </Typography>
    );
  }
  return (
    <Tooltip title={`${level.description} (${level.min}–${level.max}%)`}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "rgba(46, 125, 50, 0.12)",
          color: "success.dark",
          fontWeight: 700,
          fontSize: "0.875rem",
        }}
      >
        {level.level}
      </Box>
    </Tooltip>
  );
};

export const ReportCardFormModal: React.FC<ReportCardFormModalProps> = ({
  open,
  onClose,
  initialReport,
  childrenOptions = [],
  selectedChildId,
  onChildChange,
  onSave,
}) => {
  const showChildPicker = childrenOptions.length > 1 && !initialReport;
  const [form, setForm] = useState<ReportCardFormValues>(
    emptyForm(selectedChildId || undefined)
  );
  const [error, setError] = useState<string | null>(null);

  const titleOptions = useMemo(() => {
    const opts = [...REPORT_TITLE_OPTIONS] as string[];
    if (form.title && !opts.includes(form.title)) opts.unshift(form.title);
    return opts;
  }, [form.title]);

  const phaseOptions = useMemo(() => {
    const opts = [...REPORT_PHASE_OPTIONS] as string[];
    if (form.phase && !opts.includes(form.phase)) opts.unshift(form.phase);
    return opts;
  }, [form.phase]);

  useEffect(() => {
    if (!open) return;
    const currentYear = academicYearOptions()[0];
    if (initialReport) {
      const normalized = normalizeReportCard(initialReport);
      const termKey = detectPrimaryTerm(normalized);
      setForm({
        id: normalized.id,
        childId: selectedChildId || undefined,
        periodLabel: buildPeriodLabel(termKey, currentYear),
        title: normalized.title || "Progress Report",
        phase: normalized.phase || "Intermediate Phase",
        academicYear: currentYear,
        termKey,
        daysAbsent:
          normalized.daysAbsent != null ? String(normalized.daysAbsent) : "",
        subjects:
          normalized.subjects.length > 0
            ? normalized.subjects
            : defaultSubjects(),
        teacherComment: normalized.teacherComment || normalized.notes || "",
        teacherSignedAt: normalized.teacherSignedAt?.slice(0, 10) || "",
        principalComment: normalized.principalComment || "",
        principalSignedAt: normalized.principalSignedAt?.slice(0, 10) || "",
        schoolClosesOn: normalized.schoolClosesOn?.slice(0, 10) || "",
        schoolReopensOn: normalized.schoolReopensOn?.slice(0, 10) || "",
        overallResult: normalized.overallResult || "",
        issuedDate: normalized.issuedDate?.slice(0, 10) || "",
        notes: normalized.notes || "",
      });
    } else {
      setForm(emptyForm(selectedChildId || undefined));
    }
    setError(null);
  }, [open, initialReport, selectedChildId]);

  const selectedChild = childrenOptions.find(
    (c) => c.id === (form.childId || selectedChildId)
  );

  const subtitle = selectedChild
    ? `${initialReport ? "Edit" : "Create"} a report card for ${getChildFullName(selectedChild)}.`
    : initialReport
      ? "Update this report card."
      : "Create a CAPS-style report card.";

  const setTermAndYear = (termKey: ReportTermKey, academicYear: string) => {
    setForm((prev) => ({
      ...prev,
      termKey,
      academicYear,
      periodLabel: buildPeriodLabel(termKey, academicYear),
    }));
  };

  const updateSubjectPercent = (subjectId: string, raw: string) => {
    const value = parseOptionalNumber(raw);
    setForm((prev) => ({
      ...prev,
      subjects: prev.subjects.map((s) => {
        if (s.id !== subjectId) return s;
        return {
          ...s,
          terms: {
            ...s.terms,
            [prev.termKey]: {
              ...s.terms?.[prev.termKey],
              percent: value,
            },
          },
        };
      }),
    }));
  };

  const handleSubmit = () => {
    if (showChildPicker && !form.childId) {
      setError("Select which family member this report card is for.");
      return;
    }
    if (!form.academicYear.trim()) {
      setError("Academic year is required.");
      return;
    }
    if (form.subjects.some((s) => !s.name.trim())) {
      setError("Every subject needs a name.");
      return;
    }
    const periodLabel = buildPeriodLabel(form.termKey, form.academicYear);
    onSave({
      ...form,
      periodLabel,
      notes: form.teacherComment || form.notes,
    });
    onClose();
  };

  return (
    <ActionModal
      open={open}
      onClose={onClose}
      title={initialReport ? "Edit report card" : "Add report card"}
      subtitle={subtitle}
      maxWidth="md"
      footer={
        <Box
          sx={{
            display: "flex",
            gap: 1,
            justifyContent: "flex-end",
            width: "100%",
          }}
        >
          <NoticeboardButton
            variant="secondary"
            size={ACTION_MODAL_FOOTER_PRIMARY_SIZE}
            onClick={onClose}
          >
            Cancel
          </NoticeboardButton>
          <NoticeboardButton
            variant="primary"
            size={ACTION_MODAL_FOOTER_PRIMARY_SIZE}
            onClick={handleSubmit}
          >
            Save report card
          </NoticeboardButton>
        </Box>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        {error ? (
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        ) : null}

        {showChildPicker ? (
          <FormControl fullWidth size="small">
            <InputLabel id="report-card-child-label">Family member</InputLabel>
            <Select
              labelId="report-card-child-label"
              label="Family member"
              value={form.childId || ""}
              onChange={(e) => {
                const next = String(e.target.value);
                setForm((prev) => ({ ...prev, childId: next }));
                onChildChange?.(next);
              }}
            >
              {childrenOptions.map((child) => (
                <MenuItem key={child.id} value={child.id}>
                  {getChildFullName(child)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : null}

        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1.25 }}>
            Report details
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 1.5,
            }}
          >
            <TextField
              label="Academic year"
              size="small"
              value={form.academicYear}
              InputProps={{ readOnly: true }}
            />

            <FormControl fullWidth size="small">
              <InputLabel id="report-term-label">Term</InputLabel>
              <Select
                labelId="report-term-label"
                label="Term"
                value={
                  TERM_SELECT_OPTIONS.includes(form.termKey)
                    ? form.termKey
                    : "term1"
                }
                onChange={(e) =>
                  setTermAndYear(
                    e.target.value as ReportTermKey,
                    form.academicYear
                  )
                }
              >
                {TERM_SELECT_OPTIONS.map((term) => (
                  <MenuItem key={term} value={term}>
                    {REPORT_TERM_LABELS[term]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel id="report-title-label">Title</InputLabel>
              <Select
                labelId="report-title-label"
                label="Title"
                value={form.title}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, title: String(e.target.value) }))
                }
              >
                {titleOptions.map((title) => (
                  <MenuItem key={title} value={title}>
                    {title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel id="report-phase-label">Phase</InputLabel>
              <Select
                labelId="report-phase-label"
                label="Phase"
                value={form.phase}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, phase: String(e.target.value) }))
                }
              >
                {phaseOptions.map((phase) => (
                  <MenuItem key={phase} value={phase}>
                    {phase}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Issued date"
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={form.issuedDate}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, issuedDate: e.target.value }))
              }
            />

            <TextField
              label="Days absent"
              type="number"
              size="small"
              inputProps={{ min: 0 }}
              value={form.daysAbsent}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, daysAbsent: e.target.value }))
              }
            />
          </Box>
        </Box>

        <Box>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1.25 }}
          >
            <Typography variant="subtitle2">Subjects & marks</Typography>
            <NoticeboardButton
              variant="secondary"
              size="small"
              startIcon={<AddIcon />}
              onClick={() =>
                setForm((prev) => ({
                  ...prev,
                  subjects: [...prev.subjects, createSubjectRow("")],
                }))
              }
            >
              Add subject
            </NoticeboardButton>
          </Stack>

          <Box
            sx={{
              display: { xs: "none", sm: "grid" },
              gridTemplateColumns: "minmax(0, 1.6fr) 140px 120px 40px",
              gap: 1,
              px: 0.5,
              mb: 0.75,
            }}
          >
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Subject
            </Typography>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                Percentage (%)
              </Typography>
              <Tooltip title="Enter the learner’s mark as a percentage (0–100).">
                <InfoOutlinedIcon sx={{ fontSize: 14, color: "text.secondary" }} />
              </Tooltip>
            </Stack>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Achievement level
            </Typography>
            <span />
          </Box>

          <Stack spacing={1}>
            {form.subjects.map((subject) => {
              const percent = subject.terms?.[form.termKey]?.percent;
              return (
                <Box
                  key={subject.id}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "minmax(0, 1.6fr) 140px 120px 40px",
                    },
                    gap: 1,
                    alignItems: "center",
                  }}
                >
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Subject name"
                    value={subject.name}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        subjects: prev.subjects.map((s) =>
                          s.id === subject.id
                            ? { ...s, name: e.target.value }
                            : s
                        ),
                      }))
                    }
                  />
                  <TextField
                    size="small"
                    type="number"
                    inputProps={{ min: 0, max: 100 }}
                    value={percent ?? ""}
                    onChange={(e) =>
                      updateSubjectPercent(subject.id, e.target.value)
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">%</InputAdornment>
                      ),
                    }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: { xs: "flex-start", sm: "center" },
                    }}
                  >
                    <AchievementBadge percent={percent} />
                  </Box>
                  <IconButton
                    size="small"
                    aria-label="Remove subject"
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        subjects: prev.subjects.filter(
                          (s) => s.id !== subject.id
                        ),
                      }))
                    }
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              );
            })}
          </Stack>

          <Box
            sx={{
              mt: 1.5,
              p: 1.25,
              borderRadius: 2,
              bgcolor: "rgba(2, 136, 209, 0.08)",
              display: "flex",
              gap: 1,
              alignItems: "flex-start",
            }}
          >
            <InfoOutlinedIcon
              sx={{ fontSize: 18, color: lightTheme.colors.primary, mt: 0.15 }}
            />
            <Typography variant="body2" color="text.secondary">
              Achievement levels are calculated automatically based on CAPS
              guidelines. You can enter percentages only.
            </Typography>
          </Box>
        </Box>
      </Box>
    </ActionModal>
  );
};
